import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";

import type { DepositAccountRow, DepositAccountsCorridor } from "../types/depositAccounts.types";

/**
 * Session-scoped access latch (mirrors `useRuntimeDeposits`): a missing `payments:read` role surfaces as a
 * 401 from the RBAC guard, and every 401 costs a silent `/auth/refresh` + replay inside HttpApiService —
 * after the first denial we stop asking for the rest of the session.
 */
const deniedPrincipals = new Set<string>();

export interface UseDepositAccountsResult {
  accounts: DepositAccountRow[];
  /** True while the selected org's list has not settled yet. False when no org is selected. */
  isLoading: boolean;
  /** Non-access fetch failure — the caller renders an error alert, never an empty list as truth. */
  hasError: boolean;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  refresh: () => Promise<void>;
}

/**
 * Active deposit receiving accounts of ONE organization for one corridor (`organization_id` is REQUIRED by
 * the endpoint — there is no list-all). Pass `null` while no org is selected: nothing is fetched and the list
 * is empty. All result state is KEYED by the org+corridor it was loaded for and derived against the current
 * selection, so switching orgs (or corridors) can never show the previous selection's rows (and no
 * synchronous resets are needed in effects).
 */
export const useDepositAccounts = (
  corridor: DepositAccountsCorridor,
  organizationId: string | null,
): UseDepositAccountsResult => {
  const { user } = useAuth();
  const deniedKey = user?.id ?? "anon";
  const fetchKey = organizationId ? `${corridor.id}:${organizationId}` : null;

  const [loaded, setLoaded] = useState<{ key: string; rows: DepositAccountRow[] } | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [settledKey, setSettledKey] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(() => !deniedPrincipals.has(deniedKey));

  // Only the latest issued request may write state: a slow response for a previously-selected org must
  // never clobber the current org's settled/loading state (it would leave the view loading forever).
  const requestSeq = useRef(0);

  const accounts = loaded && loaded.key === fetchKey ? loaded.rows : [];
  const hasError = !!fetchKey && errorKey === fetchKey;
  const isLoading = !!fetchKey && hasAccess && settledKey !== fetchKey;

  const fetchList = useCallback(async () => {
    if (!organizationId || !fetchKey || deniedPrincipals.has(deniedKey)) return;
    const seq = ++requestSeq.current;
    try {
      const rows = await corridor.listByOrganization(organizationId);
      if (seq !== requestSeq.current) return;
      setLoaded({ key: fetchKey, rows });
      setErrorKey(null);
    } catch (err: unknown) {
      if (seq !== requestSeq.current) return;
      if (HttpApiService.isAccessDeniedError(err)) {
        deniedPrincipals.add(deniedKey);
        setHasAccess(false);
      } else {
        setErrorKey(fetchKey);
      }
      setLoaded({ key: fetchKey, rows: [] });
    } finally {
      if (seq === requestSeq.current) setSettledKey(fetchKey);
    }
  }, [corridor, organizationId, fetchKey, deniedKey]);

  useEffect(() => {
    if (!fetchKey) return;
    void fetchList();
  }, [fetchKey, fetchList]);

  return { accounts, isLoading, hasError, hasAccess, refresh: fetchList };
};
